import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SESSION_EXPIRED_EVENT, setWidgetLinkId } from "../services/api";
import { useSessionStore } from "../store/useSessionStore";
import { useStore } from "../store/globalState";
import { APP_ROUTES } from "../constants";
import { LINK_ERROR_CODE } from "../utils/commons";
import { clearConsumerIdentityBind } from "../auth/consumerIdentityCache";
import {
  applyDeadLinkPartnerRecovery,
  partnerRecoveryFromErrorDetails,
} from "../utils/dead-link-recovery";

/**
 * Listens for dead-link events (LINK_NOT_FOUND / LINK_EXPIRED / LINK_REVOKED /
 * 410). Expired/revoked go to SessionExpiredPage (partner return CTA when
 * configured). LINK_NOT_FOUND stays on ErrorPage with no CTA.
 */
export function useSessionExpiredHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const resetSession = useSessionStore((state) => state.resetSession);
  const setLinkId = useStore((state) => state.setLinkId);

  useEffect(() => {
    const handleSessionExpired = (event: Event) => {
      if (
        location.pathname === APP_ROUTES.ERROR_PAGE ||
        location.pathname === APP_ROUTES.SESSION_EXPIRED_PAGE
      ) {
        return;
      }

      const detail = (
        event as CustomEvent<{ code?: string; details?: unknown }>
      ).detail;
      const isRevoked = detail?.code === LINK_ERROR_CODE.LINK_REVOKED;
      const isNotFound = detail?.code === LINK_ERROR_CODE.LINK_NOT_FOUND;
      const isRequesterBlocked =
        isRevoked &&
        typeof detail?.details === "object" &&
        detail.details !== null &&
        (detail.details as { reasonCode?: unknown }).reasonCode ===
          "REQUESTER_BLOCKED";
      const errorCode = isRevoked
        ? LINK_ERROR_CODE.LINK_REVOKED
        : isNotFound
          ? LINK_ERROR_CODE.LINK_NOT_FOUND
          : LINK_ERROR_CODE.LINK_EXPIRED;

      applyDeadLinkPartnerRecovery(
        partnerRecoveryFromErrorDetails(detail?.details),
      );

      resetSession();
      clearConsumerIdentityBind();
      setWidgetLinkId(null);
      setLinkId(null);

      if (isRequesterBlocked) {
        navigate(APP_ROUTES.REQUEST_ALREADY_FULFILLED_PAGE, {
          replace: true,
        });
        return;
      }

      if (isNotFound) {
        navigate(APP_ROUTES.ERROR_PAGE, {
          replace: true,
          state: {
            title: t("error.linkExpired"),
            subTitle: null,
            message: t("error.linkExpiredMessage"),
            btnText: null,
            isOut: false,
            errorCode,
          },
        });
        return;
      }

      navigate(APP_ROUTES.SESSION_EXPIRED_PAGE, { replace: true });
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);

    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    };
  }, [navigate, location.pathname, t, resetSession, setLinkId]);
}
